import type { Journey } from "../../types/journey";

// Minimal structural view of a live UserStepState row — keeps this module
// decoupled from the journey-progress hook (which carries React deps).
interface StepStateLike {
	stepId: string;
	status: string;
	openedAt?: string | null;
	closedAt?: string | null;
}

export interface JourneyChatStep {
	id: string;
	name: string;
	status: string; // not_started | in_progress | complete
	order: number;
	totalSteps: number;
}

// The journey context era-web sends in the /api/chat body so Eve can ground
// answers in the user's actual stage (handoff onboarding-v5-eve-journey-step-context).
export interface JourneyChatContext {
	journeyType: string;
	journeyName: string;
	currentStep: JourneyChatStep;
}

// Derive the current journey step from the CDN catalog + live step states.
// Mirrors the My Journey page's current-step pick (Journey.tsx): most-recent
// in-progress live step → most-recent completed → CDN "current" → first step.
// Returns null when there is no resolvable journey/step (donor, early parenting,
// unsupported journey, or nothing started) → caller omits journeyContext.
export function deriveJourneyChatContext(
	journey: Journey | null,
	stepStates: Map<string, StepStateLike>,
): JourneyChatContext | null {
	if (!journey || journey.steps.length === 0) return null;

	const liveStepIds = new Set(journey.steps.map((s) => s.id));
	const inProgress = Array.from(stepStates.values())
		.filter((s) => s.status === "in_progress" && liveStepIds.has(s.stepId))
		.sort((a, b) => (b.openedAt ?? "").localeCompare(a.openedAt ?? ""));
	const completed = Array.from(stepStates.values())
		.filter((s) => s.status === "complete" && liveStepIds.has(s.stepId))
		.sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? ""));

	const currentId =
		inProgress[0]?.stepId ??
		completed[0]?.stepId ??
		journey.steps.find((s) => s.status === "current")?.id ??
		journey.steps[0]?.id;

	const step = journey.steps.find((s) => s.id === currentId);
	if (!step) return null;

	return {
		journeyType: journey.type,
		journeyName: journey.name,
		currentStep: {
			id: step.id,
			name: step.title,
			status: stepStates.get(step.id)?.status ?? "not_started",
			order: step.order,
			totalSteps: journey.totalSteps,
		},
	};
}
